import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { Suspense, memo, lazy } from 'react';

import { useActiveLink, usePbsContent } from '../../context/navigation';
import { useIsMobile } from '../../hooks';
import { SkeletonSubCategoryDesktop, SkeletonSubCategoryMobile } from '../Skeleton';
import { BeautyAndTrends } from './BeautyAndTrends';
import { BestOffers } from './BestOffers';
import { Catalogues } from './Catalogues/Catalogues';
import { BURGER_MENU_CONTENT_ROOT, CATEGORY_WRAPPER } from './constants';
import { BurgerMenuContentRoot, StyledPbsMenuContent } from './styles';
import type { BurgerMenuContentProps } from './types';
import { useGetNavigationType } from './utils';

const LazyInspiration = lazy(async () => ({
  default: (await import('./FindYourProduct/FindYourProduct')).FindYourProduct,
}));

const LazyCategoryContent = lazy(async () => ({
  default: (await import('./CategoryContent/CategoryContent')).CategoryContent,
}));

const CategoryContent: FC<{ isMobile?: boolean }> = memo(({ isMobile }) => (
  <Suspense fallback={isMobile ? <SkeletonSubCategoryMobile /> : <SkeletonSubCategoryDesktop />}>
    <LazyCategoryContent />
  </Suspense>
));

export const BurgerMenuContent = ({ contentRootRef, link }: BurgerMenuContentProps) => {
  const isMobile = useIsMobile();
  const { activeLink } = useActiveLink();
  const { isShown } = usePbsContent();
  const { linkType, id } = activeLink ?? {};
  const { getTestId } = useTestId();
  const navigation = useGetNavigationType({
    activeLinkType: linkType ?? 'None',
    link,
  });

  return (
    <BurgerMenuContentRoot
      ref={contentRootRef}
      data-testid={getTestId(BURGER_MENU_CONTENT_ROOT)}
    >
      {isShown ? (
        <StyledPbsMenuContent />
      ) : navigation.isCatalogue ? (
        <Catalogues />
      ) : (
        <>
          {navigation.isCategory && id ? (
            <CategoryContent
              isMobile={isMobile}
              data-testid={getTestId(CATEGORY_WRAPPER)}
            />
          ) : null}
          <Suspense>{navigation.isInspiration ? <LazyInspiration /> : null}</Suspense>
          {navigation.isBeautyAndTrends ? <BeautyAndTrends content={activeLink?.children} /> : null}
          {navigation.isBestOffers ? <BestOffers content={activeLink?.children} /> : null}
        </>
      )}
    </BurgerMenuContentRoot>
  );
};
